import { Trans } from "@lingui/react";
import { Alert } from "@warp-ds/react/components/alert";
import { useEffect, useRef, useState } from "react";
import { isValidContactFormVariant } from "../../types/contactFormVariants.ts";
import { ObjectPosition } from "../../types/objectPosition.ts";
import { VisibleActions } from "../components/Actions.tsx";
import { ContactFormModal } from "../components/modals/ContactFormModal.tsx";
import { ContactFormModalv2 } from "../components/modals/ContactFormModalv2.tsx";
import { ContactFormModalv3 } from "../components/modals/ContactFormModalv3.tsx";
import { EidModal } from "../components/modals/EidModal.tsx";
import { LoginModal } from "../components/modals/LoginModal.tsx";
import { useValidDealerContext } from "../context/DealerProviderContext.tsx";
import { visibleActionsHelper } from "../utils/visibleActionsHelper.ts";
import "./dealer-actions.css";

const eventId = "dealer-visibility-change";

export const ContactDealerActions = () => {
  const {
    itemData: { active },
    external: { isPreview, isLoggedIn, eidAuth, newContactForm, newContactFormV2, newContactFormV3 },
  } = useValidDealerContext();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (active && containerRef.current) {
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
          // Send event when the element is visible
          window.dispatchEvent(new CustomEvent(eventId, { detail: { isBeneathActions: true } }));
        } else {
          // Send event when the element is not visible
          window.dispatchEvent(new CustomEvent(eventId, { detail: { isBeneathActions: false } }));
        }
      });
      observer.observe(containerRef.current);
      return () => {
        observer.disconnect();
      };
    }
  }, [active]);

  if (!active && !isPreview) {
    return (
      <Alert type="negative" show>
        <h3 role="alert" className="t4 -mt-2">
          <Trans id="dealer.actions.inactive.title" message="This ad is no longer available" />
        </h3>
        <p>
          <Trans id="dealer.actions.inactive.description" message="The seller can't be contacted about this vehicle." />
        </p>
      </Alert>
    );
  }

  const contactFormModals = {
    v1: <ContactFormModal />,
    v2: <ContactFormModalv2 />,
    v3: <ContactFormModalv3 />,
  };

  const resolveContactFormModal = () => {
    if (newContactFormV3 && isValidContactFormVariant(newContactFormV3)) {
      return contactFormModals[newContactFormV3];
    }
    return newContactFormV2 ? contactFormModals.v2 : contactFormModals.v1;
  };

  return (
    <>
      {!isLoggedIn && <LoginModal />}
      {newContactForm && isLoggedIn && (eidAuth.userMustVerify ? <EidModal /> : resolveContactFormModal())}
      <div ref={containerRef} className="flex flex-col gap-16 pt-8">
        <VisibleActions position={ObjectPosition.NonStickyCTA} />
      </div>
    </>
  );
};

export const FixedActions = () => {
  const {
    organisationData: { name, phoneNumber: orgPhoneNumber },
    itemData: { active, hideWriteToSeller },
    external: { reservationEnabled, isItemReserved },
    resolved: { phoneNumberCallbackUrl },
  } = useValidDealerContext();

  const [visibleState, setVisibleState] = useState<"visible" | "hidden" | "initial">("initial");
  const [hasFocus, setHasFocus] = useState(false);

  useEffect(() => {
    const handleVisibilityChange = (event: CustomEventInit<{ isBeneathActions: boolean }>) => {
      setVisibleState((prevValue) => {
        if (!event.detail?.isBeneathActions && prevValue === "initial") {
          return "initial";
        }
        return event.detail?.isBeneathActions ? "visible" : "hidden";
      });
    };

    window.addEventListener(eventId, handleVisibilityChange);

    return () => {
      window.removeEventListener(eventId, handleVisibilityChange);
    };
  }, []);

  const actions = visibleActionsHelper({
    position: ObjectPosition.StickyCTA,
    hideWriteToSeller,
    phoneNumberCallbackUrl,
    orgPhoneNumber,
    reservationEnabled,
    isItemReserved,
  }).length;

  if (!active || actions === 0) return null;

  return (
    <div
      className={`dealer-direction fixed z-10 bottom-0 md:bottom-a md:top-[49] left-0 right-0 s-bg dealer-animate-${hasFocus ? "visible" : visibleState}`}
      style={{ boxShadow: "0px 6px 12px 0px rgba(0, 0, 0, 0.20), 0px 10px 20px 0px rgba(0, 0, 0, 0.10)" }}
    >
      <div className="page-container flex gap-16 items-center justify-between pt-8! pb-safe-[8]">
        <div className="sr-only md:not-sr-only!">
          <b>
            <Trans id="dealer.actions.sold-by" message="Sold by" />{" "}
          </b>
          {name}
        </div>
        <div
          role="toolbar"
          onFocus={() => {
            setHasFocus(true);
          }}
          onBlur={() => {
            setHasFocus(false);
          }}
          className="flex gap-8 w-full md:w-auto items-start"
        >
          <VisibleActions position={ObjectPosition.StickyCTA} />
        </div>
      </div>
    </div>
  );
};
